import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Smartphone, Share, Plus, Download } from 'lucide-react';

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    try {
      const checkInstallStatus = () => {
        // Check if already installed
        const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                                    window.navigator.standalone ||
                                    document.referrer.includes('android-app://');

        // Detect iOS
        const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

        // Detect Android
        const android = /Android/.test(navigator.userAgent);

        // Set all states together
        setIsStandalone(isInStandaloneMode);
        setIsIOS(iOS);
        setIsAndroid(android);
        
        return { isInStandaloneMode, iOS, android };
      };
      
      const { isInStandaloneMode, iOS, android } = checkInstallStatus();

      // Check if already dismissed
      let daysSinceDismissed = 999;
      try {
        const dismissed = localStorage.getItem('pwa-install-dismissed');
        const dismissedTime = dismissed ? parseInt(dismissed) : 0;
        daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      } catch (e) {
        console.log('localStorage not available');
      }

      // Show prompt if:
      // 1. Not in standalone mode (not installed)
      // 2. On mobile (iOS or Android)
      // 3. Not dismissed in last 7 days
      if (!isInStandaloneMode && (iOS || android) && daysSinceDismissed > 7) {
        // Show after 3 seconds
        const timer = setTimeout(() => {
          setShowPrompt(true);
        }, 3000);

        return () => clearTimeout(timer);
      }

      // Listen for beforeinstallprompt event (Android Chrome)
      const handleBeforeInstall = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        if (!isInStandaloneMode && daysSinceDismissed > 7) {
          setTimeout(() => setShowPrompt(true), 3000);
        }
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstall);

      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      };
    } catch (error) {
      console.error('InstallPrompt error:', error);
    }
  }, []);

  const handleInstallClick = async () => {
    try {
      if (deferredPrompt) {
        // Android Chrome - show native install prompt
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          console.log('User accepted the install prompt');
        }
        
        setDeferredPrompt(null);
        setShowPrompt(false);
        try {
          localStorage.setItem('pwa-install-dismissed', Date.now().toString());
        } catch (e) {
          console.log('localStorage not available');
        }
      }
    } catch (error) {
      console.error('Install prompt error:', error);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    try {
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    } catch (e) {
      console.log('localStorage not available');
    }
  };

  if (!showPrompt || isStandalone) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-4 animate-fade-in">
      <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-amber-500 rounded-3xl shadow-2xl max-w-md w-full p-6 md:p-8 relative animate-slide-up">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 left-4 text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-4 rounded-2xl">
            <Smartphone className="w-12 h-12 text-black" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-white text-center mb-2">
          התקן את האפליקציה שלנו!
        </h2>
        
        <p className="text-gray-300 text-center mb-6">
          גישה מהירה לכל הרכבים, התראות על רכבים חדשים, וחוויה כמו אפליקציה אמיתית
        </p>

        {/* Benefits */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3 text-gray-200">
            <div className="bg-amber-500/20 p-2 rounded-lg">
              <Download className="w-5 h-5 text-amber-500" />
            </div>
            <span>גישה מהירה מהמסך הראשי</span>
          </div>
          <div className="flex items-center gap-3 text-gray-200">
            <div className="bg-amber-500/20 p-2 rounded-lg">
              <Smartphone className="w-5 h-5 text-amber-500" />
            </div>
            <span>חוויה כמו אפליקציה אמיתית</span>
          </div>
          <div className="flex items-center gap-3 text-gray-200">
            <div className="bg-amber-500/20 p-2 rounded-lg">
              <span className="text-xl">🔔</span>
            </div>
            <span>התראות על רכבים חדשים</span>
          </div>
        </div>

        {/* Instructions based on device */}
        {isIOS && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <span>📱</span>
              הוראות להתקנה (iPhone):
            </h3>
            <ol className="text-sm text-gray-300 space-y-2">
              <li className="flex items-start gap-2">
                <span className="bg-amber-500 text-black rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                <span>לחץ על כפתור <Share className="inline w-4 h-4 mx-1" /> <strong>&quot;שתף&quot;</strong> בתחתית המסך</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-amber-500 text-black rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                <span>גלול למטה ובחר <Plus className="inline w-4 h-4 mx-1" /> <strong>&quot;הוסף למסך הבית&quot;</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-amber-500 text-black rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                <span>לחץ <strong>&quot;הוסף&quot;</strong> בפינה הימנית העליונה</span>
              </li>
            </ol>
          </div>
        )}

        {isAndroid && deferredPrompt && (
          <Button
            onClick={handleInstallClick}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold py-6 text-lg"
          >
            <Download className="w-5 h-5 ml-2" />
            התקן עכשיו
          </Button>
        )}

        {isAndroid && !deferredPrompt && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <span>🤖</span>
              הוראות להתקנה (Android):
            </h3>
            <ol className="text-sm text-gray-300 space-y-2">
              <li className="flex items-start gap-2">
                <span className="bg-amber-500 text-black rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                <span>לחץ על <strong>תפריט</strong> (3 נקודות) בפינה העליונה</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-amber-500 text-black rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                <span>בחר <strong>&quot;הוסף למסך הבית&quot;</strong> או <strong>&quot;התקן אפליקציה&quot;</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-amber-500 text-black rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                <span>אשר את ההתקנה</span>
              </li>
            </ol>
          </div>
        )}

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="text-gray-400 hover:text-white text-sm mt-4 w-full text-center transition-colors"
        >
          אולי מאוחר יותר
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
